import onMarkAsReadUnread from 'owa-mail-commands/lib/actions/onMarkAsReadUnread';
import type { QuickStepAction } from 'owa-mail-quick-steps-store/lib/store/schema/QuickStepAction';
import type { QuickStepActionType } from 'owa-mail-quick-steps-store/lib/store/schema/QuickStepActionType';
import type QuickStepComposeValueType from 'owa-mail-quick-steps-store/lib/store/schema/QuickStepComposeValueType';
import { getQuickStepActionsById } from 'owa-mail-quick-steps-store/lib/util/getQuickStepById';
import type { ActionSource } from 'owa-analytics-types';
import { lazyMountAndShowFullOptions } from 'owa-options-view';
import { getActionSourceFromCommandingViewMode } from 'owa-command-ribbon';
import onDelete from 'owa-mail-commands/lib/actions/onDelete';
import { onMoveToFolderClicked, onCopyClicked } from './onMoveToCommandActions';
import { onCategoryClicked, onClearCategoriesClicked } from './onCategorizeCommandActions';
import { onForwardAsAttachment, onForward } from 'owa-mail-commands/lib/actions/onResponse';
import type EmailAddressWrapper from 'owa-service/lib/contract/EmailAddressWrapper';
import { getQuickStepActionsByHotkey } from 'owa-mail-quick-steps-store/lib/util/getQuickStepActionsByHotkey';
import type { QuickStepHotkeyId } from 'owa-mail-quick-steps-store/lib/store/schema/QuickStepHotkeyId';

export const onExecuteQuickStep = (id: string) => () => {
    executeQuickStep(getActionSourceFromCommandingViewMode(), id);
};

export function onExecuteQuickStepHotkey(actionSource: ActionSource, hotkey: QuickStepHotkeyId) {
    const actions = getQuickStepActionsByHotkey(hotkey);
    actions.forEach(action => executeAction(action, actionSource));
}

export const onManageQuickStepsClicked = () => {
    lazyMountAndShowFullOptions.importAndExecute('mail', 'quickSteps');
};

// Helper functions
function executeQuickStep(actionSource: ActionSource, id: string) {
    const actions = getQuickStepActionsById(id);
    for (let i = Destructiveness.NonDestructiveGoFirst; i < Destructiveness.Max; i++) {
        actions.forEach(action => {
            if (getDestructivenessFromActionType(action.type) === i) {
                executeAction(action, actionSource);
            }
        });
    }
}

enum Destructiveness {
    NonDestructiveGoFirst,
    NonDestructive,
    NonDestructiveGoLast,
    Destructive,
    Max,
}

const ActionTypeToDestructivenessMap: Map<QuickStepActionType, Destructiveness> = new Map<
    QuickStepActionType,
    Destructiveness
>([
    ['markAsRead', Destructiveness.NonDestructive],
    ['markAsUnread', Destructiveness.NonDestructive],
    ['delete', Destructiveness.Destructive],
    ['moveTo', Destructiveness.Destructive],
    ['copyTo', Destructiveness.NonDestructiveGoLast],
    ['category', Destructiveness.NonDestructive],
    ['clearCategories', Destructiveness.NonDestructiveGoFirst],
    ['forwardAsAttachment', Destructiveness.NonDestructiveGoLast],
    ['forwardTo', Destructiveness.NonDestructiveGoLast],
]);

function getDestructivenessFromActionType(type: QuickStepActionType) {
    const destructiveness = ActionTypeToDestructivenessMap.get(type);
    if (destructiveness !== undefined) {
        return destructiveness;
    } else {
        throw new Error('Action type ' + type + ' was not added to destructiveness map');
    }
}

function executeAction(action: QuickStepAction, actionSource: ActionSource) {
    switch (action.type) {
        case 'markAsRead':
            onMarkAsReadUnread(actionSource, true);
            break;
        case 'markAsUnread':
            onMarkAsReadUnread(actionSource, false);
            break;
        case 'delete':
            onDelete(actionSource, actionSource);
            break;
        case 'moveTo':
            onMoveToFolderClicked(action.extraInfo! as string);
            break;
        case 'copyTo':
            onCopyClicked(action.extraInfo! as string);
            break;
        case 'category':
            onCategoryClicked(action.extraInfo as string, false /*isChecked*/);
            break;
        case 'clearCategories':
            onClearCategoriesClicked();
            break;
        case 'forwardAsAttachment':
            onForwardAsAttachment(actionSource, {
                to: (action.extraInfo! as QuickStepComposeValueType)
                    .toRecipients as EmailAddressWrapper[],
            });
            break;
        case 'forwardTo':
            onForward(actionSource, {
                to: (action.extraInfo! as QuickStepComposeValueType)
                    .toRecipients as EmailAddressWrapper[],
            });
            break;
        default:
            break;
    }
}
